import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAui } from '@assistant-ui/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useListMcpChatSuggestionsQuery } from '../api/mcpApi';
import type { McpChatSuggestionQuestionDto } from '../types/mcpApiTypes';
import { McpChatSuggestionsPreload } from './McpChatSuggestionsPreload';
import { buildSuggestionUserMessage } from '../utils/suggestionMarkers';
import { setPendingSuggestionKey } from '../utils/pendingSuggestionKey';
import { useOptionalMcpChatOnboarding } from '../hooks/McpChatOnboardingContext';

type CategoryFilter = 'all' | string;

function appendPrompt(
  aui: ReturnType<typeof useAui>,
  prompt: string
): void {
  const trimmed = prompt.trim();
  if (!trimmed) return;
  try {
    const thread =
      typeof (aui as { thread?: unknown }).thread === 'function'
        ? (
            aui as {
              thread: () => { append: (message: string) => void };
            }
          ).thread()
        : (
            aui as {
              thread: { append: (message: string) => void };
            }
          ).thread;
    thread.append(trimmed);
  } catch (error) {
    console.error('Failed to send chat suggestion', error);
  }
}

function fillComposer(
  aui: ReturnType<typeof useAui>,
  text: string
): void {
  try {
    const composer =
      typeof (aui as { composer?: unknown }).composer === 'function'
        ? (
            aui as {
              composer: () => { setText: (value: string) => void };
            }
          ).composer()
        : (
            aui as {
              composer: { setText: (value: string) => void };
            }
          ).composer;
    composer.setText(text);
    window.requestAnimationFrame(() => {
      const input = document.querySelector<HTMLTextAreaElement>(
        '.aui-composer-input, textarea[aria-label="Message input"]'
      );
      input?.focus();
      const cursor = text.length;
      input?.setSelectionRange(cursor, cursor);
    });
  } catch (error) {
    console.error('Failed to fill composer from chat suggestion', error);
  }
}

function resolveInteractionMode(
  item: McpChatSuggestionQuestionDto
): 'send' | 'compose' | 'collect' {
  if (item.interactionMode) return item.interactionMode;
  if (item.inputs?.length) return 'collect';
  if (item.composeText?.trim()) return 'compose';
  return 'send';
}

export const McpChatSuggestions: FC = () => {
  const aui = useAui();
  const navigate = useNavigate();
  const onboarding = useOptionalMcpChatOnboarding();
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError } = useListMcpChatSuggestionsQuery();

  const categories = data?.categories ?? [];
  const questions = data?.questions ?? [];
  const previewCount = data?.previewCount ?? 4;

  const filtered = useMemo(() => {
    if (category === 'all') return questions;
    return questions.filter(item => item.categoryKey === category);
  }, [category, questions]);

  const preview = filtered.slice(0, previewCount);
  const rest = filtered.slice(previewCount);
  const hasMore = rest.length > 0;

  // The FAQ block is bottom-anchored; the extra items overflow below it so
  // nothing above moves — reveal them by scrolling the thread viewport.
  const restRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!expanded) return;
    const el = restRef.current;
    const viewport = el?.closest<HTMLElement>(
      '[data-slot="aui_thread-viewport"]'
    );
    if (!el || !viewport) return;
    const elRect = el.getBoundingClientRect();
    const vpRect = viewport.getBoundingClientRect();
    const delta = Math.min(
      elRect.bottom - vpRect.bottom,
      elRect.top - vpRect.top
    );
    if (delta > 0) {
      viewport.scrollTo({ top: viewport.scrollTop + delta, behavior: 'smooth' });
    }
  }, [expanded]);

  const onPick = (item: McpChatSuggestionQuestionDto) => {
    if (onboarding && !onboarding.assertChatAllowed()) return;
    const mode = resolveInteractionMode(item);
    if (mode === 'collect' && item.inputs?.length) {
      // Steps open on their own page, then start a new chat (like skills).
      setPendingSuggestionKey(null);
      navigate(`/chat/faq/${encodeURIComponent(item.key)}`);
      return;
    }
    if (mode === 'compose' && item.composeText?.trim()) {
      setPendingSuggestionKey(item.key);
      fillComposer(aui, item.composeText);
      return;
    }
    setPendingSuggestionKey(null);
    appendPrompt(aui, buildSuggestionUserMessage(item.prompt, item.key));
  };

  const onCategoryChange = (next: CategoryFilter) => {
    setCategory(next);
    setExpanded(false);
  };

  if (isLoading) {
    return <McpChatSuggestionsPreload />;
  }

  if (isError || questions.length === 0) {
    return null;
  }

  return (
    <div
      className="mcp-chat-suggestions flex w-full flex-col gap-2 px-1"
      dir="rtl"
      aria-label="سوالات متداول"
    >
      <div className="px-0.5">
        <span className="text-foreground text-xs font-semibold">
          سوالات متداول
        </span>
      </div>

      <div
        className="flex w-full gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label="دسته‌بندی ابزار"
      >
        <Button
          type="button"
          role="tab"
          aria-selected={category === 'all'}
          variant={category === 'all' ? 'default' : 'outline'}
          size="xs"
          className="shrink-0 rounded-full"
          onClick={() => onCategoryChange('all')}
        >
          همه
        </Button>
        {categories.map(cat => (
          <Button
            key={cat.id}
            type="button"
            role="tab"
            aria-selected={category === cat.key}
            variant={category === cat.key ? 'default' : 'outline'}
            size="xs"
            className="shrink-0 rounded-full"
            onClick={() => onCategoryChange(cat.key)}
          >
            {cat.title}
          </Button>
        ))}
      </div>

      <div className="grid w-full grid-cols-2 content-start gap-2">
        {preview.map(item => (
          <SuggestionItem key={item.id} item={item} onPick={onPick} />
        ))}
      </div>

      {hasMore ? (
        // Keeps the collapsed height in flow so the block never shifts; the
        // expanded list is absolutely positioned and overflows downward.
        <div className="relative">
          {expanded ? (
            <>
              <div aria-hidden className="h-8" />
              <div
                ref={restRef}
                className="absolute inset-x-0 top-0 flex flex-col gap-2 pb-4"
              >
                <div className="grid w-full grid-cols-2 content-start gap-2">
                  {rest.map(item => (
                    <SuggestionItem key={item.id} item={item} onPick={onPick} />
                  ))}
                </div>
                <div className="flex items-start justify-center">
                  <MoreToggleButton
                    expanded
                    onClick={() => setExpanded(false)}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-start justify-center">
              <MoreToggleButton
                expanded={false}
                onClick={() => setExpanded(true)}
              />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const SuggestionItem: FC<{
  item: McpChatSuggestionQuestionDto;
  onPick: (item: McpChatSuggestionQuestionDto) => void;
}> = ({ item, onPick }) => (
  <Button
    type="button"
    variant="outline"
    onClick={() => onPick(item)}
    className={cn(
      'text-foreground hover:bg-muted border-border/60 h-auto min-h-0 w-full items-start justify-start rounded-xl px-3 py-2 text-start font-normal whitespace-normal shadow-xs'
    )}
  >
    <span className="text-sm leading-snug">{item.label}</span>
  </Button>
);

const MoreToggleButton: FC<{ expanded: boolean; onClick: () => void }> = ({
  expanded,
  onClick
}) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    className="h-8 min-w-28 gap-1.5 rounded-full px-4 text-xs font-medium"
    onClick={onClick}
  >
    {expanded ? (
      <>
        کمتر
        <ChevronUpIcon className="size-3.5 opacity-70" />
      </>
    ) : (
      <>
        بیشتر
        <ChevronDownIcon className="size-3.5 opacity-70" />
      </>
    )}
  </Button>
);
